import {ErrorContext, getErrorContext} from './errorContext';
import logger, {defaultLogPath} from './logging';
import fs from 'fs';
import {promisify} from 'util';
import {resolvePath} from './utils';

function getErrorHtml(error: any): string | HTMLElement {
    return '<b>' + (error?.message || error || '').toString() + '</b><hr>' +
        (error?.stack?.toString() || '');
}

function getDlcListHtml(details: ErrorContext) {
    const getTextStatus = (status: boolean | null) => {
        switch (status) {
            case null:
                return 'unknown';
            case false:
                return 'disabled';
            case true:
                return 'enabled';
        }
    };

    const dlcListItems = details.game.dlc
        .map((dlc) => `<li>${dlc.name}: ${getTextStatus(dlc.isEnabled)}`);

    return `
        <ul style="font-size: smaller">
            ${dlcListItems.join('\n')}
        </ul>
    `;
}

function getModsListHtml(details: ErrorContext) {
    const modsListItems = details.mods
        .map((mod) => {
            const displayedName = mod.displayedName ? `${mod.displayedName} (${mod.name})` : mod.name;
            const version = mod.version ? ` v${mod.version}` : '';
            return `<li>${displayedName}${version} (source: ${mod.source})\n`;
        })

    if (!details.mods.length) {
        return '';
    }

    return `Mods:
        <ul style="font-size: smaller">
            ${modsListItems.join('\n')}
        </ul>
    `;
}

export async function getLogs(indentSize: number = 2, maxLines = 5000) {
    const logFileUrls = [
        defaultLogPath,
        resolvePath('../ErrorLog.txt'),
        resolvePath('../log.txt')
    ]

    const readFileAsync = fs.promises?.readFile
        ?? promisify(fs.readFile) as typeof fs.promises.readFile;

    const indent = ' '.repeat(indentSize);

    const logFiles: string[] = [];
    for (const logUrl of logFileUrls) {
        const isJson = logUrl === defaultLogPath;
        const logLines: string[] = [];

        try {
            const logText = await readFileAsync(logUrl, {encoding: 'utf8'});
            if (!logText) {
                continue;
            }

            let offset = logText.length - 1;
            while (logLines.length < maxLines && offset >= 0) {
                const nextOffset = logText.lastIndexOf('\n', offset - 1);
                const line = logText.slice(nextOffset + 1, offset).trimEnd();
                offset = nextOffset;
                if (line) {
                    logLines.push(`${indent}${indent}${indent}${isJson ? line : JSON.stringify(line)}`);
                }
            }
        } catch (err) {
            logger.warn({file: logUrl, err}, `Unable to read log file`);
            continue;
        }

        logFiles.push(
            `${indent}${indent}${JSON.stringify(logUrl)}: [\n`
            + logLines.reverse().join(',\n') + '\n'
            + `${indent}${indent}]`
        );
    }

    return `${indent}"logs": {\n`
        + logFiles.join(',\n') + '\n'
        + `${indent}}`;
}

export function getReportHtml(error: any) {
    const errorContext = getErrorContext(error);
    const gameDetails = errorContext.game.version ? `Game v${errorContext.game.version}` : 'Unknown game version';
    const dlcDetails = getDlcListHtml(errorContext);
    const modsDetails = getModsListHtml(errorContext);
    const errorDetails = `
        <pre style="font-size: smaller">${getErrorHtml(errorContext.error)}</pre>
    `;

    return `
        <div style="text-align: left">
            ${errorDetails}
            ${gameDetails}
            ${dlcDetails}
            ${modsDetails}
        </div>
    `;
}

export function getFooterHtml(): string | HTMLElement {
    const createLinkHtml = (text: string, url: string) =>
        `<a href="#" onclick="GameStartUpWebSite.getInstance().execute('${url}')">` +
        text.replaceAll(' ', '&nbsp;') +
        `</a>`;

    const inviteLink = createLinkHtml('server invite', 'https://discord.gg/remtairy');
    const channelLink = getErrorContext().mods.some((mod) => mod.name !== 'FastCutIns')
        ? createLinkHtml('mod bugs channel', 'https://discord.com/channels/454295440305946644/1114325651269369946')
        : createLinkHtml('bugs channel', 'https://discord.com/channels/454295440305946644/591771080985673729')

    return `
        <div style="font-size: small">
            If you don\'t understand error message, use "Copy report" button to copy report to clipboard
            and post it in ${channelLink} (${inviteLink}).
        </div>
    `;
}
